/**
 * Duplicating, copying and pasting entities, as pure functions. A copy is the
 * entities' subtrees as a level file writes them (manifest entities), so the
 * clipboard, Duplicate and Paste all end in one `entity.create` per subtree,
 * and a copy pastes into any level.
 *
 * Placing copies follows Unreal: fresh ids throughout, names numbered where a
 * sibling already has them, references among the copied entities follow the
 * copies, and references to entities the level doesn't have are cleared.
 * Reference props are found through the component schemas, never by name.
 */
import type { CommandOrigin, CommandRequest } from "../../commands/types";
import { MANIFEST_FORMAT_VERSION } from "../../assets/content";
import type { ManifestEntity } from "../../assets/content";
import { upgradeEntities } from "../../assets/manifestUpgrades";
import type { EntityId, EntityRecord } from "../../world/entity";
import { isLevelRoot, levelRootOf, topmostOf } from "../../world/hierarchy";
import type { ComponentRegistry } from "../../world/schema/ComponentRegistry";
import type { ComponentSchema } from "../../world/schema/defineComponent";
import type { PropType } from "../../world/schema/propTypes";
import { serializeWorld } from "../../world/serialization";
import type { WorldSnapshot } from "../../world/World";
import type { SelectionState } from "../selection/Selection";
import { deleteEdit } from "../selection/selectionEdits";
import type { RequestGroup } from "../selection/selectionEdits";
import { freshId, uniqueName } from "./createModel";

type Records = ReadonlyMap<EntityId, EntityRecord>;
type Hierarchy = Pick<WorldSnapshot, "entities" | "roots">;

/** Where a copy goes: under `parent`, in front of `before` (last when absent). */
interface Placement {
  readonly parent: EntityId | null;
  readonly before?: EntityId;
}

/** Each topmost entity's subtree (never the level root), as a level file writes it. */
export function copyOf(
  entities: Records,
  registry: ComponentRegistry,
  ids: readonly EntityId[],
): ManifestEntity[] {
  const copyable = ids.filter((id) => {
    const record = entities.get(id);
    return record !== undefined && !isLevelRoot(record);
  });
  return serializeWorld(
    { levelId: null, entities, roots: topmostOf(entities, copyable) },
    registry,
  );
}

/**
 * Duplicates `ids` in place (Ctrl+D): each copy goes right after its original,
 * as Unreal and Unity duplicate, and the copies become the selection. One undo
 * step; null when nothing can be duplicated.
 */
export function duplicateEdit(
  world: Hierarchy,
  registry: ComponentRegistry,
  ids: readonly EntityId[],
  origin: CommandOrigin,
): RequestGroup | null {
  const copies = copyOf(world.entities, registry, ids);
  const placements = copies.map((copy) => placementAfter(world, copy.id as EntityId));
  return placeEdit("Duplicate", copies, placements, world.entities, registry, origin);
}

/**
 * Pastes copies as siblings right after the active entity, or under the level
 * root when nothing (or the level root) is active, and selects them.
 */
export function pasteEdit(
  copies: readonly ManifestEntity[],
  world: Hierarchy,
  registry: ComponentRegistry,
  active: EntityId | null,
  origin: CommandOrigin,
): RequestGroup | null {
  const target = pasteTarget(world, active);
  const placements = copies.map(() => target);
  return placeEdit("Paste", copies, placements, world.entities, registry, origin);
}

/**
 * Cut's delete (the copy is taken first, by the caller): Delete's edit, named
 * for the undo history as Unreal names it.
 */
export function cutEdit(
  entities: Records,
  selection: SelectionState,
  ids: readonly EntityId[],
  origin: CommandOrigin,
): RequestGroup | null {
  const deletion = deleteEdit(entities, selection, ids, origin);
  return deletion && { ...deletion, label: deletion.label.replace(/^Delete/, "Cut") };
}

// ─── Clipboard ───────────────────────────────────────────────────────────

/**
 * Marks clipboard text as copied entities, so pasting other text does nothing.
 * Its `formatVersion` is the level format the entities are written in, so a
 * copy from an older build still pastes (upgraded like a manifest).
 */
const CLIPBOARD_KIND = "kn.entities";

/** Clipboard text for copying `ids` (Ctrl+C, Copy, Cut), or null when none can be copied. */
export function copiedText(
  entities: Records,
  registry: ComponentRegistry,
  ids: readonly EntityId[],
): string | null {
  const copies = copyOf(entities, registry, ids);
  return copies.length > 0 ? clipboardText(copies) : null;
}

/** Copies as clipboard text: JSON another tab (or a text editor) can hold. */
export function clipboardText(copies: readonly ManifestEntity[]): string {
  return JSON.stringify({
    kind: CLIPBOARD_KIND,
    formatVersion: MANIFEST_FORMAT_VERSION,
    entities: copies,
  });
}

/**
 * The copies in clipboard text, or null when it holds something else. Only
 * the envelope is checked here: `entity.create` validates each entity.
 */
export function parseClipboard(text: string): ManifestEntity[] | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;

  const { kind, formatVersion, entities } = parsed as Record<string, unknown>;
  if (kind !== CLIPBOARD_KIND || typeof formatVersion !== "number") return null;
  if (formatVersion < 1 || formatVersion > MANIFEST_FORMAT_VERSION) return null;
  if (!Array.isArray(entities) || !entities.every(isEntityTree)) return null;
  return upgradeEntities(entities as ManifestEntity[], formatVersion);
}

/**
 * Whether a value has the ids and names placing copies works with, all the way
 * down. Everything else (components, values) is `entity.create`'s to check.
 */
function isEntityTree(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const { id, name, children } = value as Record<string, unknown>;
  if (typeof id !== "string" || typeof name !== "string") return false;
  return children === undefined || (Array.isArray(children) && children.every(isEntityTree));
}

// ─── Placing copies ──────────────────────────────────────────────────────

/** Creates the copies at their placements and selects them, as one step. */
function placeEdit(
  verb: string,
  copies: readonly ManifestEntity[],
  placements: readonly Placement[],
  entities: Records,
  registry: ComponentRegistry,
  origin: CommandOrigin,
): RequestGroup | null {
  const placed = rekeyCopies(copies, placements, entities, registry);
  const [first] = placed;
  if (!first) return null;

  const creates: CommandRequest[] = placed.map((entity, i) => ({
    type: "entity.create",
    payload: { ...placements[i], entity },
    origin,
  }));
  const ids = placed.map((entity) => entity.id);
  return {
    // Named after the originals, as the undo history reads "Duplicate Box".
    label:
      copies.length === 1
        ? `${verb} ${copies[0]?.name ?? first.name}`
        : `${verb} ${copies.length} entities`,
    requests: [...creates, { type: "selection.set", payload: { ids, active: ids.at(-1) }, origin }],
  };
}

/**
 * The copies with fresh ids throughout and root names unique where they go,
 * then with their references remapped. Ids are all assigned first, so a
 * reference from one copy into another follows it too.
 */
function rekeyCopies(
  copies: readonly ManifestEntity[],
  placements: readonly Placement[],
  entities: Records,
  registry: ComponentRegistry,
): ManifestEntity[] {
  const takenIds = new Set<string>(entities.keys());
  const namesUnder = new Map<EntityId | null, string[]>();
  const newIds = new Map<string, string>();

  const renamed = copies.map((copy, i) => {
    const parent = placements[i]?.parent ?? null;
    const siblings = namesUnder.get(parent) ?? childNames(entities, parent);
    const name = uniqueName(copy.name, siblings);
    namesUnder.set(parent, [...siblings, name]);
    return withFreshIds({ ...copy, name }, takenIds, newIds);
  });

  const follow = (ref: string) => newIds.get(ref) ?? (entities.has(ref as EntityId) ? ref : null);
  return renamed.map((copy) => withRemappedRefs(copy, registry, follow));
}

function withFreshIds(
  entity: ManifestEntity,
  taken: Set<string>,
  newIds: Map<string, string>,
): ManifestEntity {
  const id = freshId(entity.name, (candidate) => taken.has(candidate));
  taken.add(id);
  newIds.set(entity.id, id);

  const { children, ...rest } = entity;
  return {
    ...rest,
    id,
    ...(children && { children: children.map((child) => withFreshIds(child, taken, newIds)) }),
  };
}

/** The entity tree with every entity reference passed through `follow`. */
function withRemappedRefs(
  entity: ManifestEntity,
  registry: ComponentRegistry,
  follow: (ref: string) => string | null,
): ManifestEntity {
  const { components, children, ...rest } = entity;
  return {
    ...rest,
    ...(components && {
      components: Object.fromEntries(
        Object.entries(components).map(([type, data]) => [
          type,
          remapComponent(registry.get(type), data, follow),
        ]),
      ),
    }),
    ...(children && {
      children: children.map((child) => withRemappedRefs(child, registry, follow)),
    }),
  };
}

function remapComponent(
  schema: ComponentSchema | undefined,
  data: unknown,
  follow: (ref: string) => string | null,
): unknown {
  // Unknown components and malformed data pass through: entity.create refuses them.
  if (!schema || typeof data !== "object" || data === null) return data;

  const remapped: Record<string, unknown> = { ...data };
  for (const [prop, type] of Object.entries(schema.props)) {
    if (prop in remapped) remapped[prop] = remapValue(type, remapped[prop], follow);
  }
  return remapped;
}

function remapValue(
  type: PropType,
  value: unknown,
  follow: (ref: string) => string | null,
): unknown {
  if (type.type === "entityRef") return typeof value === "string" ? follow(value) : value;
  if (type.type === "array" && Array.isArray(value))
    return value.map((item) => remapValue(type.of, item, follow));
  return value;
}

// ─── Where copies go ─────────────────────────────────────────────────────

/** Right after `id`, under the same parent. */
function placementAfter(world: Hierarchy, id: EntityId): Placement {
  const parent = world.entities.get(id)?.parent ?? null;
  const siblings = childIds(world, parent);
  const next = siblings[siblings.indexOf(id) + 1];
  return { parent, ...(next !== undefined && { before: next }) };
}

function pasteTarget(world: Hierarchy, active: EntityId | null): Placement {
  const record = active === null ? undefined : world.entities.get(active);
  if (record && !isLevelRoot(record)) return placementAfter(world, record.id);
  return { parent: levelRootOf(world.entities) };
}

function childIds(world: Hierarchy, parent: EntityId | null): readonly EntityId[] {
  return parent === null ? world.roots : (world.entities.get(parent)?.children ?? []);
}

function childNames(entities: Records, parent: EntityId | null): string[] {
  const records = [...entities.values()].filter((record) => record.parent === parent);
  return records.map((record) => record.name);
}
